/**
 * Design token references for CMS-authored content.
 *
 * CMS components resolve every visual value through a `--dcm-*` custom property so
 * merchant theme customization and Ecommerce Pro SDK themes can restyle CMS pages
 * without replacing components. Matching the existing `--dcm-cms-*` usage in this
 * directory, CMS-only tokens are consumed with a literal fallback rather than being
 * declared centrally in `theme/core-menu-variables.ts`.
 *
 * Per-instance values chosen in the editor are applied as `--cms-*` properties, which
 * take precedence over these theme-level defaults. See `heading.tsx` for the pattern.
 */

/** Builds a CSS `var()` reference with a literal visual fallback. */
export function token(name: string, fallback: string): string {
  return `var(${name}, ${fallback})`;
}

/**
 * Reads a keyed style value, falling back when the key is absent.
 *
 * Saved page data is unvalidated JSON, so a persisted key may not exist in the current
 * map — a page saved before an option was renamed, or hand-edited content. TypeScript
 * types the prop as the union, so this lookup is what actually makes it safe at runtime.
 *
 * Uses an own-property check rather than `in`, which would walk the prototype chain and
 * happily return `toString` as a CSS value.
 */
export function lookup<T>(map: Readonly<Record<string, T>>, key: string, fallback: T): T {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : fallback;
}

/**
 * Semantic text colors, mapped onto the storefront's global color roles so CMS text
 * inherits merchant branding. `link` intentionally points at the merchant-editable
 * buttons/links color rather than a fixed blue.
 */
export const TEXT_COLOR_ROLES = {
  text: token('--dcm-color-text-primary', '#485055'),
  secondary: token('--dcm-color-text-secondary', '#a0a7ab'),
  muted: token('--dcm-color-text-muted', '#677882'),
  inverted: token('--dcm-color-text-inverted', '#ffffff'),
  link: token('--dcm-color-primary', '#0b99e6'),
} as const;

export type TextColorRole = keyof typeof TEXT_COLOR_ROLES;

/**
 * The merchant's two configurable font slots. `--dcm-font-primary` is their "Heading
 * Font" and `--dcm-font-secondary` their "Body Font" — the naming is not obvious from
 * the variables, but `theme.utils.ts` maps `fonts.primary` from `fonts.heading`, and the
 * admin Customize → Fonts screen labels them that way. Falls back to `inherit` so an
 * unconfigured store keeps whatever the surrounding page uses.
 */
export const FONT_ROLES = {
  heading: token('--dcm-font-primary', 'inherit'),
  body: token('--dcm-font-secondary', 'inherit'),
} as const;

export type FontRole = keyof typeof FONT_ROLES;

/**
 * Background fills for text blocks. `none` is the default: without a fill a block's box
 * is invisible, which makes width and padding controls look like they do nothing.
 */
export const SURFACE_COLOR_ROLES = {
  none: 'transparent',
  surface: token('--dcm-color-surface', '#ffffff'),
  muted: token('--dcm-color-surface-muted', '#f5f6f7'),
} as const;

export type SurfaceColorRole = keyof typeof SURFACE_COLOR_ROLES;

const TEXT_COLOR_ROLE_KEYS = Object.keys(TEXT_COLOR_ROLES) as TextColorRole[];
const SURFACE_COLOR_ROLE_KEYS = Object.keys(SURFACE_COLOR_ROLES) as SurfaceColorRole[];

export function isSurfaceColorRole(value: string): value is SurfaceColorRole {
  return (SURFACE_COLOR_ROLE_KEYS as string[]).includes(value);
}

/**
 * Resolves a stored background color value to a CSS color.
 *
 * Mirrors {@link resolveTextColor}'s role-key-or-literal contract: the stored value is
 * either a surface role key (`'surface'`) or a custom hex the author entered
 * (`'#ff0000'`). This single-field approach lets the background control offer both theme
 * roles and a custom hex without a second prop to keep in sync.
 *
 * Unrecognized values fall back to `SURFACE_COLOR_ROLES.none` (transparent) rather than
 * a visible color. An invalid or partially-typed hex emitted verbatim would *set* the
 * custom property, making the `var()` fallback unreachable and painting an unexpected fill
 * over an entire page band. This is not a behavior change: before this resolver existed,
 * an unrecognized string was emitted verbatim as a CSS value, which the browser discards
 * as an invalid declaration and treats as if the property were not set — i.e. transparent.
 */
export function resolveSurfaceColor(value: string | undefined): string {
  if (value === undefined) return SURFACE_COLOR_ROLES.none;
  if (isSurfaceColorRole(value)) return SURFACE_COLOR_ROLES[value];
  return COMPLETE_HEX.test(value) ? value : SURFACE_COLOR_ROLES.none;
}

/**
 * Any complete CSS hex color — 3, 4, 6 or 8 digits.
 *
 * Deliberately broader than what the current editor widget emits (6 digits): pages saved
 * before the color-role field existed used a picker with an alpha slider, so `#rrggbbaa`
 * and shorthand are already out there and must keep rendering as authored.
 */
const COMPLETE_HEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Resolves a stored color value to a CSS color.
 *
 * The value is either a role key (`'text'`) or a literal the merchant entered
 * (`'#ff0000'`), which lets a single field offer both theme roles and a custom
 * color without a second prop to keep in sync.
 *
 * Anything else falls back to the text role. That matters for two reasons. Saved page
 * data is unvalidated JSON, so this — not the editor widget — is the only place a
 * non-color can be stopped before it reaches a CSS declaration. And a half-typed hex
 * like `#ff` would otherwise be emitted verbatim: because the custom property would be
 * *set*, the `var()` fallback never engages, the declaration is invalid at
 * computed-value time, and the text silently inherits instead of using the theme.
 */
export function resolveTextColor(value: string): string {
  if (isTextColorRole(value)) return TEXT_COLOR_ROLES[value];
  return COMPLETE_HEX.test(value) ? value : TEXT_COLOR_ROLES.text;
}

export function isTextColorRole(value: string): value is TextColorRole {
  return (TEXT_COLOR_ROLE_KEYS as string[]).includes(value);
}

/**
 * Visual heading scale, deliberately separate from the global `--dcm-font-size-*`
 * scale: that scale tops out at 20px because it sizes body and menu text, which is
 * far too small for page-builder headings.
 */
export const HEADING_PRESETS = {
  h1: {
    fontSize: token('--dcm-cms-heading-1-font-size', '48px'),
    fontWeight: token('--dcm-cms-heading-1-font-weight', '700'),
  },
  h2: {
    fontSize: token('--dcm-cms-heading-2-font-size', '40px'),
    fontWeight: token('--dcm-cms-heading-2-font-weight', '700'),
  },
  h3: {
    fontSize: token('--dcm-cms-heading-3-font-size', '32px'),
    fontWeight: token('--dcm-cms-heading-3-font-weight', '600'),
  },
  h4: {
    fontSize: token('--dcm-cms-heading-4-font-size', '24px'),
    fontWeight: token('--dcm-cms-heading-4-font-weight', '600'),
  },
  h5: {
    fontSize: token('--dcm-cms-heading-5-font-size', '20px'),
    fontWeight: token('--dcm-cms-heading-5-font-weight', '600'),
  },
  h6: {
    fontSize: token('--dcm-cms-heading-6-font-size', '18px'),
    fontWeight: token('--dcm-cms-heading-6-font-weight', '600'),
  },
} as const;

export type HeadingPreset = keyof typeof HEADING_PRESETS;

export const HEADING_LINE_HEIGHT = token('--dcm-cms-heading-line-height', '1.2');

/**
 * Line-height multipliers for headings.
 *
 * Heading line-heights are unitless multipliers. System default inherits the theme-level
 * `--dcm-cms-heading-line-height` (fallback 1.2). Authorable presets provide explicit
 * typographic rhythm:
 * - `tight` (1.1) — compact title stacking for large display headings.
 * - `comfortable` (1.3) — standard multi-line heading readability.
 * - `spacious` (1.5) — relaxed heading cadence.
 */
export const HEADING_LINE_HEIGHTS = {
  tight: token('--dcm-cms-heading-line-height-tight', '1.1'),
  comfortable: token('--dcm-cms-heading-line-height-comfortable', '1.3'),
  spacious: token('--dcm-cms-heading-line-height-spacious', '1.5'),
} as const;

export type HeadingLineHeight = keyof typeof HEADING_LINE_HEIGHTS | 'system';

/**
 * Body text scale. Unlike headings, this maps straight onto the storefront's global
 * `--dcm-font-size-*` scale — that scale exists to size body and menu copy, so CMS
 * paragraphs pick up any merchant or SDK override of it for free.
 */
export const BODY_TEXT_SIZES = {
  xs: token('--dcm-font-size-xs', '12px'),
  sm: token('--dcm-font-size-sm', '13px'),
  md: token('--dcm-font-size-md', '14px'),
  lg: token('--dcm-font-size-lg', '16px'),
  xl: token('--dcm-font-size-xl', '18px'),
  xxl: token('--dcm-font-size-2xl', '20px'),
} as const;

export type BodyTextSize = keyof typeof BODY_TEXT_SIZES;

export const BODY_LINE_HEIGHT = token('--dcm-line-height-normal', '1.43');

/**
 * Line-height multipliers for body text.
 *
 * Body line-heights are unitless multipliers. System default inherits the theme-level
 * `--dcm-line-height-normal` (fallback 1.43). Authorable presets provide explicit
 * typographic rhythm:
 * - `tight` (1.25) — compact paragraph spacing.
 * - `comfortable` (1.5) — standard body readability.
 * - `spacious` (1.75) — relaxed reading cadence for long-form copy.
 */
export const BODY_LINE_HEIGHTS = {
  tight: token('--dcm-cms-body-line-height-tight', '1.25'),
  comfortable: token('--dcm-cms-body-line-height-comfortable', '1.5'),
  spacious: token('--dcm-cms-body-line-height-spacious', '1.75'),
} as const;

export type BodyLineHeight = keyof typeof BODY_LINE_HEIGHTS | 'system';

/** Merchant-configured corner radius for buttons (admin Customize -> Theme). */
export const BUTTON_RADIUS = token('--dcm-radius-buttons', '6px');

/** The merchant's configured buttons/links color, used as the CTA accent. */
export const BRAND_COLOR = token('--dcm-color-primary', '#2563eb');

/** Readable label color on top of {@link BRAND_COLOR}. */
export const BRAND_CONTRAST_COLOR = token('--dcm-color-text-inverted', '#ffffff');

/**
 * Button inner padding — effectively its tap target — and label weight.
 *
 * Exposed as tokens rather than literals so an Ecommerce Pro SDK theme can resize
 * buttons without replacing the component, per the marketplace styling rule.
 */
export const BUTTON_INSET = token('--dcm-cms-button-inset', '12px 24px');
export const BUTTON_FONT_WEIGHT = token('--dcm-cms-button-font-weight', '500');

/**
 * FAQ rhythm and weight. Themeable but not editor controls: an author picks colors, sizes and the
 * block's outer padding, while the internal spacing between a question, its answer and the disclosure
 * glyph is the component's own vertical rhythm — the kind of thing an Ecommerce Pro SDK theme wants
 * to set globally rather than per instance.
 */
export const FAQ_QUESTION_FONT_WEIGHT = token('--dcm-cms-faq-question-font-weight', '600');
export const FAQ_QUESTION_GAP = token('--dcm-cms-faq-question-gap', '12px');
export const FAQ_QUESTION_INSET = token('--dcm-cms-faq-question-inset', '16px 0');
export const FAQ_HEADING_GAP = token('--dcm-cms-faq-heading-gap', '16px');
export const FAQ_ANSWER_INSET = token('--dcm-cms-faq-answer-inset', '0 0 16px');
export const FAQ_DIVIDER_WIDTH = token('--dcm-cms-faq-divider-width', '1px');

/** Reading-width constraints for text blocks. */
export const MAX_WIDTHS = {
  narrow: token('--dcm-cms-max-width-narrow', '480px'),
  normal: token('--dcm-cms-max-width-normal', '720px'),
  none: 'none',
} as const;

export type MaxWidthOption = keyof typeof MAX_WIDTHS;

/**
 * Width constraints for page-level section containers — distinct from {@link MAX_WIDTHS}, which is a
 * typographic reading-measure scale for text blocks (heading, paragraph, FAQ). Use this scale for
 * Section; use MAX_WIDTHS for leaf text components.
 *
 * Key names intentionally mirror MAX_WIDTHS (`narrow`, `normal`, `none`) even though the pixel values
 * differ. Saved Section data holds those exact key strings, so reusing them means every previously-saved
 * `maxWidth` value still resolves to a valid entry. If we renamed the keys, a page saved with
 * `maxWidth: 'normal'` would miss the lookup, fall through Section's literal-passthrough branch, and
 * emit `max-width: normal` — an invalid CSS declaration that is silently discarded, rendering the
 * section full-bleed with no error anywhere.
 *
 * Value rationale (values are invented for the alpha, not design-sourced — revisit before GA):
 *
 * - **`narrow` 768px** — equals MAX_WIDTHS.normal (720px) plus Section's default 24px horizontal
 *   padding on each side. This is the narrowest band that still lets a child text block's own
 *   `maxWidth` choice bind: a container narrower than 720px would swallow the text block's control
 *   and the author would see nothing happen. It also aligns with the marketplace's `largePhone`
 *   breakpoint (`src/theme/constants/media.ts`).
 *
 * - **`normal` 992px** — the marketplace's `desktop` breakpoint. Reads as narrower than the
 *   surrounding storefront without collapsing to a single reading column.
 *
 * - **`wide`** — tracks `--dct--customized-maxSiteWidth`, the merchant-configurable site width
 *   declared in `src/theme/core-menu-variables.ts` and applied by
 *   `components/core/content-wrapper.jsx`, so a wide section lines up with the header, menu and
 *   product grid, and keeps lining up if a merchant changes that setting. The `1200px` literal is
 *   only the fallback for when that variable is absent. Note the nested `var()`: the outer
 *   `--dcm-cms-container-wide` is the CMS-level override hook; the inner is the storefront value.
 *   The storefront variable is emitted with a `px` unit (see core-menu-variables.ts line 74), so
 *   the nested reference is a valid `max-width` value.
 */
export const CONTAINER_WIDTHS = {
  narrow: token('--dcm-cms-container-narrow', '768px'),
  normal: token('--dcm-cms-container-normal', '992px'),
  wide: token('--dcm-cms-container-wide', 'var(--dct--customized-maxSiteWidth, 1200px)'),
  none: 'none',
} as const;

export type ContainerWidthOption = keyof typeof CONTAINER_WIDTHS;

/** Block sizing: shrink to content, or fill the available width. */
export const BLOCK_WIDTHS = {
  fit: 'fit-content',
  fill: '100%',
} as const;

/**
 * Minimum-height presets for flex layout containers — lets an author give a band visual
 * presence even when its content slot is empty.
 *
 * Key rationale (values are invented for the alpha, not design-sourced — revisit before GA):
 *
 * - **`auto`** — no minimum; the container collapses to its content, which is the correct
 *   behavior for inline or tightly-fitted layouts.
 *
 * - **`medium` ~320px** — a content band with breathing room. Gives a row of cards or a
 *   text-and-image split enough height to feel intentional without forcing full-screen space.
 *
 * - **`tall` ~480px** — hero-sized. Appropriate for a banner or a feature section that
 *   should dominate the viewport fold on a phone.
 *
 * - **`full` 100vh** — fills the viewport height. Note that in the Puck editor this resolves
 *   against the **canvas iframe** height, not the real viewport; the live page resolves it
 *   against the browser window as expected.
 */
export const CONTAINER_MIN_HEIGHTS = {
  auto: 'auto',
  medium: token('--dcm-cms-min-height-medium', '320px'),
  tall: token('--dcm-cms-min-height-tall', '480px'),
  full: '100vh',
} as const;

export type ContainerMinHeight = keyof typeof CONTAINER_MIN_HEIGHTS;

export type BlockWidthOption = keyof typeof BLOCK_WIDTHS;

/**
 * Vertical rhythm scale for block-level gaps. Deliberately not the global
 * `--dcm-space-*` scale, which tops out at 24px because it spaces the insides of
 * components rather than the gaps between page sections.
 */
export const BLOCK_SPACING = {
  xs: token('--dcm-cms-space-xs', '8px'),
  sm: token('--dcm-cms-space-sm', '16px'),
  md: token('--dcm-cms-space-md', '24px'),
  lg: token('--dcm-cms-space-lg', '40px'),
  xl: token('--dcm-cms-space-xl', '64px'),
  xxl: token('--dcm-cms-space-2xl', '96px'),
} as const;

export type BlockSpacing = keyof typeof BLOCK_SPACING;

export type BoxSpacing = {
  top: string;
  right: string;
  bottom: string;
  left: string;
};

export const NO_SPACING: BoxSpacing = { top: '0px', right: '0px', bottom: '0px', left: '0px' };

/** Serializes a four-sided spacing object into a CSS shorthand value. */
export function spacingToCss(spacing: BoxSpacing | undefined): string {
  const { top, right, bottom, left } = spacing ?? NO_SPACING;
  return `${top} ${right} ${bottom} ${left}`;
}

/**
 * Image aspect-ratio presets, expressed as CSS `aspect-ratio` values.
 *
 * `original` deliberately maps to `auto` rather than a ratio: `auto` tells the browser to use the
 * image's own intrinsic width/height (or, once loaded, its natural dimensions) instead of imposing
 * one, which is exactly "don't crop this image to a preset shape". Mapping it to e.g. `1 / 1` would
 * silently crop every image an author has not deliberately reshaped.
 */
export const IMAGE_ASPECT_RATIOS = {
  original: 'auto',
  square: '1 / 1',
  landscape: '4 / 3',
  wide: '16 / 9',
  portrait: '3 / 4',
} as const;

export type ImageAspectRatio = keyof typeof IMAGE_ASPECT_RATIOS;

/**
 * Corner-radius presets for the Image block's "Corners" control.
 *
 * `subtle` and `rounded` used to reuse the merchant-configurable `--dcm-radius-*` scale
 * (`--dcm-radius-sm` / `--dcm-radius-md`) that also drives button and card corners. That was the
 * PO's main click-test complaint on this block (ECOMM-12193): on a live instance those tokens
 * resolve to 4px and 8px, which the mechanism DOES apply -- the theme-token plumbing was never
 * broken -- but 4px/8px is a button/card-scale radius. Rounding one corner of a 360-800px image
 * by 4 or 8 pixels reads as noise, not a deliberate "rounded corners" choice; a PO clicking
 * through the presets could see *something* move but not anything they'd call a design decision.
 * This is the same shape of bug Section (page-band width) and Image's own `width`/{@link
 * IMAGE_SIZES} control already hit and were corrected for: borrowing a scale sized for a
 * different UI element instead of giving this control its own scale anchored at the element's
 * actual on-screen size.
 *
 * The fix, matching that precedent exactly: Image gets its OWN `--dcm-cms-image-radius-*` tokens,
 * not a share of the button/card scale.
 *
 * `pill` and `circle` are still NOT theme tokens, deliberately, and that part of the original
 * reasoning is unchanged. They are geometric shapes relative to the element's own box (a
 * fully-rounded end, or a circle), not a brand-styling choice — no merchant radius setting could
 * sensibly replace `9999px` or `50%` with a smaller value without breaking the shape. Do not "fix"
 * these into `--dcm-radius-*` (or the new `--dcm-cms-image-radius-*`) references; a themed radius
 * here would produce an oval instead of a pill, or an ellipse instead of a circle, the moment a
 * merchant's configured radius differs from the element's own dimensions.
 *
 * On a SQUARE image (`aspectRatio: 'square'`, or the aspect-ratio override `circle` itself forces
 * -- see `image-style.ts`'s cross-field coupling), `pill`'s `9999px` and `circle`'s `50%` clamp to
 * the exact same rendered shape: a `border-radius` greater than or equal to half of a box's
 * shorter side always draws that side as a full semicircle, so both presets round every corner to
 * a half-width arc and the two become visually IDENTICAL. That overlap is real, not a rendering
 * bug, and is disclosed in `image.tsx`'s `RADIUS_OPTIONS` labels rather than left for an author to
 * discover by toggling between them and seeing nothing change.
 *
 * Value rationale (values are invented for the alpha, not design-sourced -- revisit before GA):
 *
 * - **`subtle` 8px** -- twice the old button-radius borrow (4px), chosen to be the smallest
 *   radius that is unambiguously visible as "softened corners" at typical image widths
 *   (240-800px, see {@link IMAGE_SIZES}) without reading as a mistake or a rendering artifact
 *   the way 4px did.
 *
 * - **`rounded` 24px** -- three times `subtle`, not merely double, so the two options remain
 *   visually distinct from each other (and from `subtle`) across the whole size range rather
 *   than collapsing into "two shades of barely-rounded" the way 4px/8px did. Still well short of
 *   `pill`'s effective 50%-of-shorter-side, so `rounded` reads as "rounded corners", not
 *   "stadium/pill", at any of this block's supported widths.
 */
export const IMAGE_RADII = {
  none: '0',
  subtle: token('--dcm-cms-image-radius-subtle', '8px'),
  rounded: token('--dcm-cms-image-radius-rounded', '24px'),
  pill: '9999px',
  circle: '50%',
} as const;

export type ImageRadius = keyof typeof IMAGE_RADII;

/**
 * Size presets for the Image block, expressed as widths. Reuses {@link BLOCK_WIDTHS} for the
 * `fit`/`full` ends of the scale -- that already expresses "shrink to content" vs "fill the
 * available width", which is exactly what those two presets need -- but `small`/`medium`/`large`
 * get the Image block's OWN scale below, with their own `--dcm-cms-image-size-*` tokens.
 *
 * This scale used to reuse {@link CONTAINER_WIDTHS} (`narrow`/`normal`/`wide`), which is a
 * page-band scale sized for a full-bleed content container -- its `narrow` is 768px. That is a
 * sensible width for a Section, but a `768px` "Small" image reads as enormous inline in a page,
 * which is exactly the click-test feedback (ECOMM-12193) that prompted this scale. An Image's
 * width preset needs to be anchored to typical image sizes, not container widths, so it gets its
 * own tokens rather than continuing to borrow a scale built for a different job.
 *
 * FIX 8 (honesty correction): the paragraph that used to sit here claimed `small`/`medium`/
 * `large` were "KEPT KEYS, changed resolution" -- i.e. that re-anchoring them onto this new scale
 * would silently change already-published pages using the old `CONTAINER_WIDTHS`-based values.
 * That is false, and checkable directly: before this PR, `width` on the Image block
 * (`git show $(git merge-base HEAD origin/develop):apps/marketplace/src/cms/components/image.tsx`)
 * was `{ type: 'text', label: 'Width' }` -- a free-text CSS field, not a preset control at all, so
 * no page could have a saved `width` of the bare string `'small'`/`'medium'`/`'large'` unless an
 * author happened to type that exact literal into a free-text box (in which case it would have
 * rendered as invalid CSS then, same as now, on an unrelated key path). There is no prior preset
 * scale for these keys to have been "re-anchored" from -- they are new keys introduced by this
 * PR, sharing a value scale with the *labels* `small`/`medium`/`large`, not migrated from one.
 * The values below (240px/480px/800px) are simply this PR's first, alpha values for a
 * brand-new control; nothing published before this shipped can be affected by what they are.
 *
 * Value rationale (values are invented for the alpha, not design-sourced -- revisit before GA):
 *
 * - **`small` 240px** -- roughly a supporting/inline image (an icon-adjacent illustration or a
 *   thumbnail-scale product shot), small enough to sit beside body copy without dominating it.
 *
 * - **`medium` 480px** -- roughly half of {@link MAX_WIDTHS}.normal (720px), the CMS's own text
 *   reading-measure -- a comfortable in-content image that shares the column with a caption or
 *   adjacent text rather than spanning it.
 *
 * - **`large` 800px** -- slightly wider than that same 720px reading measure, sized to feel like
 *   a deliberate full-column feature image without reaching Section's page-band widths (this
 *   scale's own `full` preset, or Section's width control, exist for that).
 */
export const IMAGE_SIZES = {
  fit: BLOCK_WIDTHS.fit,
  small: token('--dcm-cms-image-size-small', '240px'),
  medium: token('--dcm-cms-image-size-medium', '480px'),
  large: token('--dcm-cms-image-size-large', '800px'),
  full: BLOCK_WIDTHS.fill,
} as const;

export type ImageSize = keyof typeof IMAGE_SIZES | 'custom';

/**
 * Focal point presets for the Image block, expressed as CSS `object-position` values. Backs a 3x3
 * grid widget rather than a dropdown of nine text labels, which is a friendlier control for
 * non-technical retail staff.
 */
export const IMAGE_FOCAL_POINTS = {
  topLeft: 'left top',
  topCenter: 'center top',
  topRight: 'right top',
  centerLeft: 'left center',
  center: 'center center',
  centerRight: 'right center',
  bottomLeft: 'left bottom',
  bottomCenter: 'center bottom',
  bottomRight: 'right bottom',
} as const;

export type ImageFocalPoint = keyof typeof IMAGE_FOCAL_POINTS;

/**
 * The Image block's equivalent of FlexLayout's "On mobile" control (`mobileStack`, see
 * `flex-layout.tsx`). Named and shaped to match that sibling control: an options array of
 * `{ label, value }` pairs feeding a `selectField()`, plus a derived key-union type, so the two
 * controls read as siblings in the codebase even though they govern different CSS.
 *
 * `full` makes the image stretch to fill its column's width on phones regardless of the chosen
 * Size preset — the common case, since a fixed pixel Size (e.g. "Small") authored for desktop can
 * otherwise leave awkward whitespace or force a horizontal squeeze on a narrow viewport. `keep`
 * preserves the chosen Size preset at every breakpoint, mirroring `mobileStack`'s 'keep' escape
 * hatch for authors who want identical layout everywhere. `hide` removes the whole block --
 * image and caption together, not just the image -- on phones entirely (e.g. a desktop-only
 * decorative banner) -- implemented as `display: none` at the same breakpoint as the other two,
 * applied to whichever element is the outermost wrapper for a given instance (the image itself
 * when there's no caption, or the wrapping `<figure>` when there is one, so a caption can never be
 * left dangling with no image above it), not by skipping render, so the editor can still show (and
 * an author can still select/edit) the block when previewing a mobile viewport; see image.tsx's
 * render for the editor/production divergence.
 */
export const IMAGE_MOBILE_BEHAVIOURS = {
  full: 'full',
  keep: 'keep',
  hide: 'hide',
} as const;

export type ImageMobileBehaviour = keyof typeof IMAGE_MOBILE_BEHAVIOURS;
